import { useMemo } from "react"
import axios from "axios"
import { useQuery, UseQueryResult, UseQueryOptions } from "@tanstack/react-query"

import { useAddGlobalToast } from "@app/providers/GlobalToastProvider"
import { BulkLabelBatchesResponse } from "@app/services/types"
import { extractApiErrorMessage } from "@app/utils/extractApiErrorMessage"

export const QUERY_KEY_PREFIX = "BulkLabelBatchesData"
const ERROR_TITLE = "Error Loading Bulk Label Batches"
const ERROR_MESSAGE = "There was an error loading bulk label batches. Contact support if the issue persists."

type ApiParams = {
	size?: number
	page?: number
	sort_by?: string
	sort_order?: "asc" | "desc"
}

export type FetchBulkLabelBatchesParams = ApiParams & {
	options?: Omit<UseQueryOptions<BulkLabelBatchesResponse>, "queryKey" | "queryFn" | "meta">
}

export const fetchBulkLabelBatchesData = async (params?: ApiParams) => {
	return axios
		.get<BulkLabelBatchesResponse>(`skunkworks/bulk-label-batches`, {
			params,
		})
		.then((response) => response.data)
}

export const useGetBulkLabelBatches = (
	params?: FetchBulkLabelBatchesParams
): UseQueryResult<BulkLabelBatchesResponse, Error> => {
	const { size = 25, page = 1, sort_by, sort_order, options = {} } = params ?? {}
	const addToast = useAddGlobalToast()

	const queryKey = useMemo(
		() => [QUERY_KEY_PREFIX, { size, page, sort_by, sort_order }],
		[size, page, sort_by, sort_order]
	)

	return useQuery({
		queryKey,
		queryFn: () => fetchBulkLabelBatchesData({ size, page, sort_by, sort_order }),
		meta: {
			onError: (error: Error) =>
				addToast({
					message: `${ERROR_TITLE}: ${extractApiErrorMessage(error, ERROR_MESSAGE)}`,
					severity: "error",
				}),
		},
		...options,
	})
}

export default useGetBulkLabelBatches
